import AudioEngine from '@/webgl/audio/AudioEngine'
import AudioLayeredMusic from '@/webgl/audio/AudioLayeredMusic'
import AudioBundle from '@/webgl/audio/resources/AudioBundle'
import Sound from '@/webgl/audio/Sound'

const MASTER_VOLUME = 1.0

export default class AudioMusic {
  gain: GainNode

  private _bundle: AudioBundle
  private _currentSoundId: string
  private _currentSound: Sound | AudioLayeredMusic
  private layers: Map<string, AudioLayeredMusic> = new Map()

  public get initialized(): boolean {
    return this._bundle != null
  }
  bundleLoaded: boolean = false

  constructor(readonly engine: AudioEngine) {
    this.gain = engine.context.createGain()
    this.gain.gain.value = MASTER_VOLUME
    this.gain.connect(engine.master)
    this.setVolume(0.4)
  }

  setBundle(bundle: AudioBundle) {
    this._bundle = bundle
    this._bundle?.onLoaded.on(this.onBundleLoaded)
  }

  onBundleLoaded = (bundle: AudioBundle) => {
    this.bundleLoaded = true
    this._bundle.onLoaded.off(this.onBundleLoaded)
    if (this.engine.ready && this._currentSoundId) {
      this.play(this._currentSoundId)
    }
  }

  start() {
    if (this._currentSoundId == null) return
    this.play(this._currentSoundId)
  }

  setVolume(value: number, fade: number = 0) {
    this.gain.gain.setTargetAtTime(value * MASTER_VOLUME, this.engine.context.currentTime, fade)
  }

  createLayer(id: string): AudioLayeredMusic {
    const layer = new AudioLayeredMusic(this.engine)
    this.layers.set(id, layer)
    return layer
  }

  getLayer(id: string): AudioLayeredMusic {
    return this.layers.get(id)
  }

  play(id: string) {
    const identical = this._currentSound != null && this._currentSoundId === id

    if (this._currentSound && !identical) {
      this._currentSound.stop()
    }

    this._currentSoundId = id

    if (!this.engine.ready || !this.bundleLoaded) {
      return
    }

    const isLayered = this.layers.has(id)

    if (!this.engine.library.has(id) && !isLayered) {
      this._currentSound = null
      return
    }

    if (identical) {
      return
    }

    const source = this.layers.get(id) ?? this.engine.library.get(id)

    this._currentSound = source.play({
      loop: true,
    })
    this._currentSound.gain.disconnect()
    this._currentSound.gain.connect(this.gain)
  }

  stop() {
    if (!this._currentSound) return
    this._currentSound.stop()
    this._currentSound = null
    this._currentSoundId = null
  }
}
